import { Component, createRef } from "react";
import { NSARouterMakerResult, NSABoxSecret } from "namirasoft-account-react";
import { GitLabCreateRow, GitLabRow, GitLabType } from "namirasoft-credential";
import { NSBox, NSBoxEnum, NSBoxString, NSBoxTextArea, NSColumn, NSRow } from "namirasoft-site-react";
import { CredentialEntities } from "../CredentialEntities";
import { CredentialLayout } from '../CredentialLayout';
import { CredentialProps } from "../CredentialProps";

interface GitLabEditPageProps extends NSARouterMakerResult, CredentialProps
{
    isEdit: boolean;
}

interface GitLabEditPageState
{
    type?: GitLabType;
}

export default class GitLabEditPage extends Component<GitLabEditPageProps, GitLabEditPageState>
{
    NSBoxString_Name = createRef<NSBoxString>();
    NSBoxString_Host = createRef<NSBoxString>();
    NSABoxSecret_Username = createRef<NSABoxSecret>();
    NSBoxEnum_Type = createRef<NSBoxEnum<GitLabType>>();
    NSABoxSecret_PasswordSecretID = createRef<NSABoxSecret>();
    NSABoxSecret_PATTokenSecretID = createRef<NSABoxSecret>();
    NSABoxSecret_OAuthClientIDSecretID = createRef<NSABoxSecret>();
    NSABoxSecret_OAuthClientSecretSecretID = createRef<NSABoxSecret>();
    NSABoxSecret_OAuthRefreshTokenSecretID = createRef<NSABoxSecret>();
    NSABoxSecret_OAuthAccessTokenSecretID = createRef<NSABoxSecret>();
    NSABoxSecret_SSHPublicKeySecretID = createRef<NSABoxSecret>();
    NSABoxSecret_SSHPrivateKeySecretID = createRef<NSABoxSecret>();
    NSBoxTextArea_Description = createRef<NSBoxTextArea>();

    constructor(props: GitLabEditPageProps)
    {
        super(props);
        this.state = {}
    }

    override render()
    {
        return (
            <CredentialLayout<GitLabRow, GitLabCreateRow>
                {...this.props}
                action={{
                    description: this.props.isEdit ? "This section allows you to edit a GitLab credential by updating its name, token, or description. The token is kept as a protected secret, and any application set up to use the credential applies the updated value." : "This section allows you to create a GitLab credential that stores a personal access token (PAT) for authenticating to GitLab repositories and source control by entering a name and the token. Namirasoft encrypts the sensitive data for secure access, and any Namirasoft application you set up to use it can then clone, read, and manage your repositories.",
                }}
                pages={{
                    entity: CredentialEntities.git_lab(this.props),
                    edit: {
                        ...this.props,
                        ui: {
                            tab_page_edit: {
                                editor: {
                                    getInputRow: (): GitLabCreateRow =>
                                    {
                                        let name = this.NSBoxString_Name.current?.getValue() ?? "";
                                        let host = this.NSBoxString_Host.current?.getValue() ?? "";
                                        let username = this.NSABoxSecret_Username.current?.getValue() ?? "";
                                        let type = this.NSBoxEnum_Type.current?.getValueEnumOne(GitLabType, GitLabType.PAT) ?? GitLabType.PAT;
                                        let password = this.NSABoxSecret_PasswordSecretID.current?.getValue() ?? "";
                                        let pat_token = this.NSABoxSecret_PATTokenSecretID.current?.getValue() ?? null;
                                        let oauth_client_id = this.NSABoxSecret_OAuthClientIDSecretID.current?.getValue() ?? null;
                                        let oauth_client_secret = this.NSABoxSecret_OAuthClientSecretSecretID.current?.getValue() ?? null;
                                        let oauth_access_token = this.NSABoxSecret_OAuthAccessTokenSecretID.current?.getValue() ?? null;
                                        let oauth_refresh_token = this.NSABoxSecret_OAuthRefreshTokenSecretID.current?.getValue() ?? null;
                                        let ssh_publickey = this.NSABoxSecret_SSHPublicKeySecretID.current?.getValue() ?? null;
                                        let ssh_privatekey = this.NSABoxSecret_SSHPrivateKeySecretID.current?.getValue() ?? null;
                                        let description = this.NSBoxTextArea_Description.current?.getValue() ?? "";

                                        return {
                                            workspace_id: "",
                                            name,
                                            host,
                                            username,
                                            type,
                                            password,
                                            pat_token,
                                            oauth_client_id,
                                            oauth_client_secret,
                                            oauth_access_token,
                                            oauth_refresh_token,
                                            ssh_publickey,
                                            ssh_privatekey,
                                            description,
                                            git_lab_category: [],
                                            git_lab_field: [],
                                            git_lab_tag: [],
                                        };
                                    },
                                    setRow: (row: GitLabRow) =>
                                    {
                                        this.NSBoxString_Name.current?.setValue(row.name);
                                        this.NSBoxString_Host.current?.setValue(row.host);
                                        this.NSABoxSecret_Username.current?.setValue(row.username_secret_id);
                                        this.NSBoxEnum_Type.current?.setValueOne(row.type);
                                        this.setState(() => ({ type: row.type }), () =>
                                        {
                                            this.NSABoxSecret_PasswordSecretID.current?.setValue(row.password_secret_id);
                                            this.NSABoxSecret_PATTokenSecretID.current?.setValue(row.pat_token_secret_id);
                                            this.NSABoxSecret_OAuthClientIDSecretID.current?.setValue(row.oauth_client_id_secret_id);
                                            this.NSABoxSecret_OAuthClientSecretSecretID.current?.setValue(row.oauth_client_secret_secret_id);
                                            this.NSABoxSecret_OAuthAccessTokenSecretID.current?.setValue(row.oauth_access_token_secret_id);
                                            this.NSABoxSecret_OAuthRefreshTokenSecretID.current?.setValue(row.oauth_refresh_token_secret_id);
                                            this.NSABoxSecret_SSHPublicKeySecretID.current?.setValue(row.ssh_publickey_secret_id);
                                            this.NSABoxSecret_SSHPrivateKeySecretID.current?.setValue(row.ssh_privatekey_secret_id);
                                        });
                                        this.NSBoxTextArea_Description.current?.setValue(row.description);
                                    }
                                }
                            }
                        }
                    }
                }}
            >
                <NSColumn>
                    <NSBoxString
                        ref={this.NSBoxString_Name}
                        required
                        title="Name"
                        auto_complete="new-password"
                        style={{ width: NSBox.width.double }}
                    />
                                        <NSBoxString
                        {...this.props}
                        ref={this.NSBoxString_Host}
                        title="Host"
                        required
                        style={{ width: NSBox.width.double }}
                    />
                    <NSRow>
                        <NSABoxSecret
                            {...this.props}
                            ref={this.NSABoxSecret_Username}
                            required
                            title="Username"
                            auto_complete="new-password"
                        />
                        <NSBoxEnum<GitLabType>
                            title="GitLab Type"
                            multiple={false}
                            required
                            getEnumObject={() => GitLabType}
                            onChanged={(box) => this.setState(() => ({ type: box.getValue() as GitLabType }))}
                        />
                    </NSRow>
                    {
                        this.state.type === GitLabType.Password &&
                        <NSABoxSecret
                            {...this.props}
                            ref={this.NSABoxSecret_PasswordSecretID}
                            required
                            title="Password"
                            auto_complete="new-password"
                            style={{ width: NSBox.width.double }}
                        />
                    }
                    {
                        this.state.type === GitLabType.PAT &&
                        <NSABoxSecret
                            {...this.props}
                            ref={this.NSABoxSecret_PATTokenSecretID}
                            required
                            title="PAT Token"
                            auto_complete="new-password"
                            style={{ width: NSBox.width.double }}
                        />
                    }
                    {
                        this.state.type === GitLabType.OAuth &&
                        <>
                            <NSABoxSecret
                                {...this.props}
                                ref={this.NSABoxSecret_OAuthClientIDSecretID}
                                required
                                title="OAuth Client ID"
                                auto_complete="new-password"
                                style={{ width: NSBox.width.double }}
                            />
                            <NSABoxSecret
                                {...this.props}
                                ref={this.NSABoxSecret_OAuthClientSecretSecretID}
                                required
                                title="OAuth Client Secret"
                                auto_complete="new-password"
                                style={{ width: NSBox.width.double }}
                            />
                            <NSABoxSecret
                                {...this.props}
                                ref={this.NSABoxSecret_OAuthAccessTokenSecretID}
                                required
                                title="OAuth Access Token"
                                auto_complete="new-password"
                                style={{ width: NSBox.width.double }}
                            />
                            <NSABoxSecret
                                {...this.props}
                                ref={this.NSABoxSecret_OAuthRefreshTokenSecretID}
                                required
                                title="OAuth Refresh Token"
                                auto_complete="new-password"
                                style={{ width: NSBox.width.double }}
                            />
                        </>
                    }
                    {
                        this.state.type === GitLabType.SSH &&
                        <>
                            <NSABoxSecret
                                {...this.props}
                                ref={this.NSABoxSecret_SSHPublicKeySecretID}
                                required
                                title="SSH Public Key"
                                auto_complete="new-password"
                                style={{ width: NSBox.width.double }}
                            />
                            <NSABoxSecret
                                {...this.props}
                                ref={this.NSABoxSecret_SSHPrivateKeySecretID}
                                required
                                title="SSH Private Key"
                                auto_complete="new-password"
                                style={{ width: NSBox.width.double }}
                            />
                        </>
                    }
                    <NSBoxTextArea
                        ref={this.NSBoxTextArea_Description}
                        required={false}
                        title="Description"
                        style={{ width: NSBox.width.double }}
                    />
                </NSColumn>
            </CredentialLayout >
        );
    }
}